"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useMemo } from "react";
import type { ChapterSelection, PaywallFilters } from "@/lib/paywall/bulk-access";
import type { PaywallPageData, PublishingRow } from "@/lib/paywall/queries";
import { loadedSelection } from "@/lib/paywall/selection-preview";
import { createPreviewCache } from "@/lib/paywall/preview-cache";
const PreviewContext = createContext<{
  storyId: string;
  data: PaywallPageData;
  cache: ReturnType<typeof createPreviewCache>;
} | null>(null);
export function ControlsPreviewProvider(t0) {
  const $ = _c(7);
  const {
    storyId,
    data,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createPreviewCache();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data || $[2] !== storyId) {
    t2 = {
      storyId,
      data,
      cache: t1
    };
    $[1] = data;
    $[2] = storyId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const value = t2;
  let t3;
  if ($[4] !== children || $[5] !== value) {
    t3 = <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
    $[4] = children;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function useControlPreview(t0) {
  const $ = _c(30);
  const {
    kind,
    storyId,
    filters,
    selection,
    fromRows,
    request
  } = t0;
  const context = useContext(PreviewContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createPreviewCache();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const localCache = t1;
  const scope = context?.storyId === storyId ? context : null;
  const cache = scope?.cache ?? localCache;
  let t2;
  if ($[1] !== scope || $[2] !== selection) {
    t2 = scope ? loadedSelection(scope.data.rows, scope.data.total, selection) : null;
    $[1] = scope;
    $[2] = selection;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rows = t2;
  let t3;
  if ($[4] !== filters || $[5] !== kind || $[6] !== selection || $[7] !== storyId) {
    t3 = JSON.stringify([kind, storyId, filters, selection]);
    $[4] = filters;
    $[5] = kind;
    $[6] = selection;
    $[7] = storyId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const key = t3;
  let t4;
  if ($[9] !== cache || $[10] !== fromRows || $[11] !== key || $[12] !== rows) {
    t4 = () => rows === null ? cache.peek(key) : fromRows(rows);
    $[9] = cache;
    $[10] = fromRows;
    $[11] = key;
    $[12] = rows;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== cache || $[15] !== key || $[16] !== request) {
    t5 = () => cache.load(key, request);
    $[14] = cache;
    $[15] = key;
    $[16] = request;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== cache || $[19] !== key || $[20] !== request || $[21] !== rows) {
    t6 = () => {
      if (rows === null) {
        cache.load(key, request).catch(_temp);
      }
    };
    $[18] = cache;
    $[19] = key;
    $[20] = request;
    $[21] = rows;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== cache) {
    t7 = () => cache.clear();
    $[23] = cache;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = {
      ready: t4,
      load: t5,
      prefetch: t6,
      clear: t7
    };
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function _temp() {}